import { lazy, Suspense, memo, useCallback, useState, useEffect, useMemo, useRef, type CSSProperties } from 'react'
import { useFeedConfig } from '../../contexts/feedConfigContext'
import { useI18n } from '../../i18n'
import { CardHeader } from './CardHeader'
import { CardMedia } from './CardMedia'
import { CardContent } from './CardContent'
import { CardBetTopLine } from './CardBetTopLine'
import { CardLegs } from './CardLegs'
import { CardPredictionContract } from './CardPredictionContract'
import { CardActions } from './CardActions'
import { CardTags } from './CardTags'
import { CardEditorial } from './CardEditorial'
import { SharePostButton } from './PostEngagement'
import { WinningTicketCard } from './WinningTicketCard'
import { NO_WINNING_TICKET_FOLLOW, type WinningTicketFollowControls } from './winningTicket'
import { useCardMediaTracking } from '../../hooks/useCardMediaTracking'
import { usePostEngagement } from './postEngagementState'
import { BettingChrome } from '../BettingChrome'
import {
  getActionProviderCartItems,
  getSelectionProviderKey,
  getSelections,
  getSelectionSet,
  normalizeProviderKey,
} from '../../utils/selectionSet'
import { useOddsStore } from '../../stores/oddsStore'
import { resolveCardEditorial } from '../../utils/cardEditorial'
import { feedCardInsightsEnabled } from '../../utils/marketInsights'
import { generatedCardBadgeText, generatedCardOriginStyle, generatedCardPersona, isGeneratedCardOrigin } from '../../branding'
import { GeneratedOriginBadge } from './GeneratedOriginBadge'
import type {
  CardPresentation,
  CopyEntryContext,
  FeedCreator,
  FeedItem,
  FeedTag,
  Selection,
} from '../../types/feedContract'
import type { FeedMeasurement } from '../../hooks/useFeedMeasurement'
import './FeedCard.css'

export interface FeedCardProps {
  item: FeedItem
  index: number
  preload?: boolean
  darkModeOverride?: boolean
  canFollow?: boolean
  isFollowing?: boolean
  followPending?: boolean
  showHeader?: boolean
  onToggleFollow?: () => void
  showPostEngagement?: boolean
  measurement?: FeedMeasurement
  onCopy?: (postId: string, selectionIds: string[], entry?: CopyEntryContext) => void
  /** A winning ticket's bottom Follow pill; omitted, the pill is hidden. */
  ticketFollow?: WinningTicketFollowControls
}

// Cadence for re-reconciling Kambi prices while a card stays on screen.
// Fast enough to surface live-game closures; slow enough to stay well
// inside public offering API budgets alongside the store's 5s debounce.
const ODDS_REFRESH_INTERVAL_MS = 30_000
const MarketInsights = lazy(() => import('./MarketInsights'))

function isCryptoDotComSelection(selection: Selection): boolean {
  return getSelectionProviderKey(selection) === 'cryptodotcom'
}

function isOGSelection(selection: Selection): boolean {
  return getSelectionProviderKey(selection) === 'og'
}

function safeCardPresentation(value?: CardPresentation | null): CardPresentation | null {
  if (!value || value.version !== 1) return null
  if (!['standard', 'spotlight', 'compact'].includes(value.variant)) return null
  if (!['inline', 'split', 'backdrop'].includes(value.media_treatment)) return null
  return value
}

// Every renderer shows a winning ticket as the ticket design, not a selection card.
export const FeedCard = memo(function FeedCard(props: FeedCardProps) {
  const { item, ticketFollow, index, measurement, darkModeOverride, showPostEngagement } = props
  if (item.content_origin === 'winning_ticket') {
    return (
      <WinningTicketCard
        item={item}
        index={index}
        measurement={measurement}
        darkModeOverride={darkModeOverride}
        showPostEngagement={showPostEngagement}
        {...(ticketFollow ?? NO_WINNING_TICKET_FOLLOW)}
      />
    )
  }
  return <StandardFeedCard {...props} />
})

function StandardFeedCard({
  item,
  index,
  preload = false,
  darkModeOverride,
  canFollow = false,
  showHeader = true,
  isFollowing,
  followPending = false,
  onToggleFollow,
  showPostEngagement = true,
  measurement,
  onCopy,
}: Omit<FeedCardProps, 'ticketFollow'>) {
  const { branding, offering, surface, marketWidgets } = useFeedConfig()
  const i18n = useI18n()
  const [marketVisible, setMarketVisible] = useState(typeof IntersectionObserver === 'undefined')
  const oddsObserver = useRef<IntersectionObserver | null>(null)
  const fetchAndProcessOdds = useOddsStore((state) => state.fetchAndProcessOdds)
  const clearOddsUpdate = useOddsStore((state) => state.clearOddsUpdate)
  const user: Partial<FeedCreator> = item.creator || {}
  const text = item.text || ''
  const media = item.media || []
  const selectionSet = getSelectionSet(item)
  const selections = getSelections(selectionSet)
  const isKambiCard = selections.length > 0
    && selections.every((selection) => getSelectionProviderKey(selection) === 'kambi')
  // Polling follows the book's own mode, not an embed override, so the Studio
  // preview host (custom betslip shim) still reconciles Kambi prices.
  const isKambiBook = (surface?.provider_betting_mode ?? surface?.betting_mode) === 'wapi_kambi'
  const observeCard = useCallback(
    (node: HTMLElement | null) => {
      const releaseMeasurement = measurement?.observeRef(node)
      oddsObserver.current?.disconnect()
      oddsObserver.current = null
      if (
        !node
        || !offering
        || !isKambiCard
        || !isKambiBook
        || typeof IntersectionObserver === 'undefined'
      ) {
        return releaseMeasurement
      }
      // Reconcile Kambi prices while the card is on screen: once on entry,
      // then on an interval so live-game moves, suspensions, and closures
      // reach the card without a scroll. The store debounces bursts.
      let refreshTimer: number | undefined
      const stopRefreshing = () => {
        clearInterval(refreshTimer)
        refreshTimer = undefined
      }
      const observer = new IntersectionObserver(([entry]) => {
        setMarketVisible(!!entry?.isIntersecting)
        if (entry?.isIntersecting) {
          void fetchAndProcessOdds(item.id, selectionSet, offering)
          refreshTimer ??= window.setInterval(() => {
            void fetchAndProcessOdds(item.id, selectionSet, offering)
          }, ODDS_REFRESH_INTERVAL_MS)
        } else {
          stopRefreshing()
        }
      }, { threshold: [0, 0.5] })
      observer.observe(node)
      oddsObserver.current = observer
      return () => {
        if (typeof releaseMeasurement === 'function') releaseMeasurement()
        stopRefreshing()
        observer.disconnect()
        if (oddsObserver.current === observer) oddsObserver.current = null
      }
    },
    [fetchAndProcessOdds, isKambiBook, isKambiCard, item.id, measurement, offering, selectionSet],
  )
  useEffect(() => () => {
    oddsObserver.current?.disconnect()
    clearOddsUpdate(item.id)
  }, [clearOddsUpdate, item.id])
  const cta = item.cta || null
  const postId = item.id
  const creatorId = typeof user.id === 'string' ? user.id : undefined
  const isSelectionCard = selections.length > 0
  const postType = item.kind
  const isDarkMode = darkModeOverride ?? branding.darkMode
  // Generated cards publish under a system persona nobody can follow.
  const isSystemCreator = isGeneratedCardOrigin(item.content_origin)
  const betslipPresentation = branding.presentation.betslip
  const editorial = resolveCardEditorial(item, i18n)
  const presentation = safeCardPresentation(item.card_presentation)

  const tagsByPosition = useMemo(() => {
    const grouped: Record<string, FeedTag[]> = {}
    for (const tag of item.tags || []) {
      const pos = tag.position || 'bet-topline'
      if (!grouped[pos]) grouped[pos] = []
      grouped[pos].push(tag)
    }
    return grouped
  }, [item.tags])

  const providerCartItems = getActionProviderCartItems(selectionSet)
  const isCryptoDotComCard = selections.some(isCryptoDotComSelection) || providerCartItems.some((item) => normalizeProviderKey(item.provider) === 'cryptodotcom')
  const isOGCard = selections.some(isOGSelection) || providerCartItems.some((item) => normalizeProviderKey(item.provider) === 'og')
  const usesPredictionContractCard = isCryptoDotComCard || isOGCard
  const providerClassName = isOGCard ? ' feed-card-provider-og' : isCryptoDotComCard ? ' feed-card-provider-cryptodotcom' : ''
  const cardClassName = `feed-card${providerClassName}${editorial?.personalized ? ' feed-card-personalized' : ''}${presentation ? ' feed-card-presented' : ''}`
  const shouldRenderActions = cta?.kind !== 'none' && (isSelectionCard || cta !== null)
  const { copyCount, trackCopyClick, recordCopyIntent, recordShare } = usePostEngagement(item, measurement, index)
  const mediaTracking = useCardMediaTracking(item)
  const originStyle = useMemo(
    () => generatedCardOriginStyle(branding, item.content_origin),
    [branding, item.content_origin],
  )
  const originBadge = <GeneratedOriginBadge text={generatedCardBadgeText(branding, item.content_origin)} origin={item.content_origin} />
  // Display-only: a generated origin's persona replaces the author name and
  // avatar; creatorId, follow and share keep reading the real creator.
  const author = generatedCardPersona(branding, item.content_origin, item.creator)

  return (
    <article
      ref={observeCard}
      className={cardClassName}
      data-post-id={postId}
      data-position={index}
      data-ranking-version={item.ranking_version || undefined}
      data-card-variant={presentation?.variant}
      data-media-treatment={presentation?.media_treatment}
      data-has-media={media.length > 0 ? 'true' : 'false'}
      data-betslip-variant={betslipPresentation.variant}
      data-betslip-odds-position={betslipPresentation.oddsPosition}
      data-share-label-mode={branding.presentation.shareLabelMode}
      style={originStyle as CSSProperties}
      {...(isDarkMode ? { 'data-dark-mode': '' } : {})}
    >
      {showHeader ? (
        <CardHeader
          username={author.display_name || i18n.t('common.unknownCreator')}
          avatarUrl={author.avatar_url || ''}
          tags={tagsByPosition['avatar-header']}
          creatorId={creatorId}
          canFollow={canFollow && !isSystemCreator && branding.presentation.showFollowButton}
          isFollowing={Boolean(isFollowing ?? user.is_following)}
          followPending={followPending}
          onToggleFollow={isSystemCreator ? undefined : onToggleFollow}
          isPromoted={postType === 'promotion'}
          badge={originBadge}
          action={(
            <SharePostButton
              postId={postId}
              selectionSet={selectionSet}
              enabled={showPostEngagement && item.share_enabled !== false}
              onShared={recordShare}
            />
          )}
          isSharedEntry={Boolean(item.is_shared_entry)}
        />
      ) : null}

      {tagsByPosition['below-header'] && (
        <CardTags tags={tagsByPosition['below-header']} className="card-tags-inline" />
      )}

      {presentation ? (
        <div className="card-presentation-visual">
          {media.length > 0 ? (
            <CardMedia media={media} preload={preload} postId={postId} {...mediaTracking} />
          ) : null}
          <CardEditorial presentation={presentation} />
        </div>
      ) : media.length > 0 ? (
        <CardMedia media={media} preload={preload} postId={postId} {...mediaTracking} />
      ) : null}


      {tagsByPosition['above-content'] && (
        <CardTags tags={tagsByPosition['above-content']} className="card-tags-inline" />
      )}

      {(text || editorial) && (
        <CardContent
          text={editorial?.body ?? text}
          username={editorial ? undefined : author.display_name || undefined}
          eyebrow={editorial?.eyebrow}
          title={editorial?.title}
          subtitle={editorial?.subtitle}
        />
      )}

      {isSelectionCard && (
        <BettingChrome>
          {usesPredictionContractCard ? (
            <CardPredictionContract selection={selections[0]} />
          ) : betslipPresentation.variant === 'panel' ? (
            <div className="card-betslip">
              <div className="card-betslip-panel">
                <CardBetTopLine selectionSet={selectionSet} postId={postId} tags={tagsByPosition['bet-topline']} />
                <CardLegs selectionSet={selectionSet} postId={postId} />
              </div>
            </div>
          ) : (
            <>
              <CardBetTopLine selectionSet={selectionSet} postId={postId} tags={tagsByPosition['bet-topline']} />
              <CardLegs selectionSet={selectionSet} postId={postId} />
            </>
          )}
          {isKambiCard && marketVisible && feedCardInsightsEnabled(marketWidgets) ? (
            <Suspense fallback={null}><MarketInsights postId={postId} selections={selections} visible={marketVisible} /></Suspense>
          ) : null}
        </BettingChrome>
      )}

      {/* Show bet-topline tags even without selections */}
      {!isSelectionCard && tagsByPosition['bet-topline'] && (
        <CardTags tags={tagsByPosition['bet-topline']} className="card-tags-inline" />
      )}


      {shouldRenderActions && (
        <CardActions
          selectionSet={selectionSet}
          cta={cta}
          postId={postId}
          copyCount={showPostEngagement ? copyCount : undefined}
          onCopyIntent={recordCopyIntent}
          onCopyClick={trackCopyClick}
          onCopy={onCopy}
        />
      )}
    </article>
  )
}
